import { ArticlesView } from './doc.article'
import { DocsTagsView } from './docs.tags'
import { debounce } from '@/helpers/form'
import { ComponentPublicInstance } from 'vue'
import { emitter } from '@/services/mitt'
import { nextTick } from 'vue'
import { platform } from '@/services/platform'
import { PodcastsView } from './docs.list.podcasts'
import { BooksView } from './doc.book'
import { PodcastEpisodesView } from './docs.list.podcasts.episodes'
import { ListView } from './docs.list'
import { Mixpanel } from '@/analytics/mixpanel'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { KeyExists, WaitUI } from './interfaces'
import { router } from '@/helpers/router'

/**
 * Search view.
 *
 * Responsible for interaction between the Search Vue component and the
 * rest of models: books, articles, tags to filter them.
 */
export class SearchView {
  private _vm: {
    wait: WaitUI
    searchInput: ComponentPublicInstance | null
  }

  public booksView: BooksView | null = null
  public articlesView: ArticlesView | null = null
  public tagsView: DocsTagsView | null = null
  public episodesView: PodcastEpisodesView | null = null
  public podcastsView: PodcastsView | null = null

  public search: string = ''
  public prompt: string = ''
  public searchHandler = (): void => {}
  public clearEventListeners = (): void => {}

  public hasFocus: boolean = true
  public showHeader: boolean = true
  public showTags: boolean = true
  public isSearching: boolean = false
  private isIOS: boolean = false
  private isScrolledDown: boolean = false

  private _abortController: AbortController | null = null

  constructor(
    vm: {
      wait: WaitUI
      searchInput: ComponentPublicInstance | null
    },
    searchTerm: string = '',
  ) {
    this._vm = vm
    this.search = searchTerm
  }

  /**
   * Initialize search view.
   */
  async init(): Promise<void> {
    this._updateDocViews()

    this.searchHandler = debounce(this.searchHandlerCallback, 1000)
    this.isIOS = await platform.isNativeIOSApp()
    this.showTags = this.search === ''
  }

  /**
   * Update the doc views.
   */
  private async _updateDocViews(): Promise<void> {
    const updateFunctions = [
      this._updateArticlesView.bind(this),
      this._updateBooksView.bind(this),
      this._updatePodcastEpisodesView.bind(this),
      this._updatePodcastsView.bind(this),
    ]

    // Run all functions asynchronously and store the promises in an array.
    const promises = updateFunctions.map((updateFunction) => updateFunction())
    const responses = await Promise.allSettled(promises)

    // Count failed requests.
    let failedViews = 0
    responses.forEach((response) => {
      if (response.status === 'rejected') {
        // If the response failed because it was canceled,
        // it was intentional. Don't capture the error.
        if (response.reason.code === 'ERR_CANCELED') {
          return
        }

        Sentry.captureException(response.reason)
        failedViews++
      }
    })

    // If all requests failed, display an error.
    if (failedViews >= updateFunctions.length) {
      this._vm.wait.start('Loading Search')
      this._vm.wait.start('Error Loading Search')
    }
  }

  public initEventListeners(): void {
    const onAppScroll = this.onAppScroll.bind(this)
    const onFocusOut = this.focusOutHandler.bind(this)

    emitter.on('appScroll', onAppScroll)
    this._vm.searchInput?.$el.addEventListener('focusout', onFocusOut)

    this.clearEventListeners = (): void => {
      emitter.off('appScroll', onAppScroll)
      this._vm.searchInput?.$el.removeEventListener('focusout', onFocusOut)
    }
  }

  public focusOutHandler(event: FocusEvent): void {
    const searchValue = (event?.target as any).value
    // Send Page View event on un-focusing the search input.
    if (searchValue && searchValue !== '') {
      Mixpanel.trackSearchUsage()
      Mixpanel.pageView()
    }
  }

  /**
   * Search handler callback.
   *
   * Filters books, articles and tags views when the search string is updated.
   */
  searchHandlerCallback(): void {
    const searchTerm = router().currentRoute.value.query.term as string
    const currentSearch = this.search

    // We only reload data from backend when search has updated, no need to do that on initial search load (or page refresh)
    if (!searchTerm && !this.search) {
      // Reset searching flag if search is cleared
      this.isSearching = false
      return
    }

    if (searchTerm !== this.search) {
      this.isSearching = true
      this._updateDocViews().finally(() => {
        // Only reset isSearching if the search term hasn't changed during the search
        if (this.search === currentSearch) {
          this.isSearching = false
        }
      })
    } else {
      // If search term matches, we're not searching anymore
      this.isSearching = false
    }

    // Show tags if the search input is empty.
    this.showTags = this.search === ''

    // If something was found, push the search string to the URL.
    if (
      this.booksView?.filteredDocs.length ||
      this.articlesView?.filteredDocs.length ||
      this.episodesView?.docs.length ||
      this.podcastsView?.podcasts.length
    ) {
      this.updateQueryString(this.search)
    }
  }

  /**
   * Update query string with the new search term.
   */
  updateQueryString(searchValue: string): void {
    router().push({ query: { term: searchValue } })
  }

  /**
   * Create a new instance of `ArticlesView` or filter it with
   * the current search term if it already exists.
   */
  private async _updateArticlesView(): Promise<void> {
    this._vm.wait.start('Articles')

    try {
      if (this.articlesView === null) {
        this.articlesView = await ArticlesView.createSearchView(this.search)
      } else {
        await this.articlesView.filter(this.search)
      }
    } catch (error: any) {
      // Rethrow the error so we can catch it in `this._updateDocViews()`.
      throw error
    } finally {
      this._vm.wait.end('Articles')
    }
  }

  /**
   * Create a new instance of `DocsTagsView`
   */
  private async _setupTagsView(): Promise<string[]> {
    this._vm.wait.start('Tags')

    try {
      const tags = await backend.getBookTags()
      this.tagsView = new DocsTagsView(tags, 'books', 'Book Categories')
      return tags
    } catch (error: any) {
      // Rethrow the error so we can catch it in `this._updateDocViews()`.
      throw error
    } finally {
      this._vm.wait.end('Tags')
    }
  }

  /**
   * Create a new instance of `BooksView` or filter it with
   * the current search term if it already exists.
   */
  private async _updateBooksView(): Promise<void> {
    this._vm.wait.start('Books')

    try {
      if (this.booksView === null) {
        const tags = await this._setupTagsView()
        this.booksView = await BooksView.createSearchView(tags, this.search)
      } else {
        await this.booksView.filter(this.search)
      }
    } catch (error: any) {
      // Rethrow the error so we can catch it in `this._updateDocViews()`.
      throw error
    } finally {
      this._vm.wait.end('Books')
    }
  }

  /**
   * Create a new instance of `PodcastsSearchView` or filter it with
   * the current search term if it already exists.
   */
  private async _updatePodcastsView(): Promise<void> {
    this._vm.wait.start('Podcasts')

    try {
      if (this.search) {
        this.podcastsView = await PodcastsView.createSearchView(this.search)
      } else {
        // Note regarding the abort controller:
        // it was present in the now removed search.podcasts.ts file.
        // I am not sure if we need it. The logic was like this:
        // - if there is a search term, we use the search view
        // - if there is no search term, we load all podcasts and use the
        // abort controller to cancel the previous request.
        // But in which case this is possible? The "no search term" case is
        // only relevant when we did not enter anything in the search field,
        // so how we can do this twice (so we have the previous request to
        // cancel)?
        // https://github.com/shortformhq/main/commit/671cd99e0295c048e316b2eb6216204d37ad15fb

        // Abort the previous request before sending a new one.
        this._abortController?.abort()

        // Create a new AbortController so that we can cancel
        // this request if a new query is made before it's fulfilled.
        this._abortController = new AbortController()
        const signal = this._abortController.signal

        const response = await backend.getPodcasts(signal)
        this.podcastsView = new PodcastsView(response.slice(0, 4))
      }
    } catch (error: any) {
      // Rethrow the error so we can catch it in `this._updateDocViews()`.
      throw error
    } finally {
      this._vm.wait.end('Podcasts')
    }
  }

  /**
   * Create a new instance of `PodcastEpisodesView` or filter it with
   * the current search term if it already exists.
   */
  private async _updatePodcastEpisodesView(): Promise<void> {
    this._vm.wait.start('Episodes')

    try {
      if (this.episodesView === null) {
        this.episodesView = await PodcastEpisodesView.createSearchView(
          this.search,
        )
      } else {
        await this.episodesView.filter(this.search)
      }
    } catch (error: any) {
      // Rethrow the error so we can catch it in `this._updateDocViews()`.
      throw error
    } finally {
      this._vm.wait.end('Episodes')
    }
  }

  get articlesListView(): ListView | null {
    if (!this.articlesFound()) {
      return null
    }

    const title = 'Articles'
    const viewAllLink =
      this.search && this.articlesView?.filteredDocs.length
        ? `/app/articles/search/${encodeURIComponent(this.search)}`
        : `/app/articles/list/all`

    return new ListView(title, this.articlesView, { viewAllLink })
  }

  get booksListView(): ListView | null {
    if (!this.booksFound()) {
      return null
    }

    const title = this.search ? 'Books' : 'Popular Books'
    const viewAllLink = this.search
      ? `/app/books/search/${encodeURI(this.search)}`
      : '/app/books/list/popular'

    return new ListView(title, this.booksView, { viewAllLink })
  }

  get episodesListView(): ListView | null {
    if (!this.episodesFound()) {
      return null
    }

    const title = 'Podcast Episodes'
    const viewAllLink = this.search
      ? `/app/podcasts/episodes/search/${this.search}`
      : '/app/podcasts'
    const viewAllText = this.search ? 'View all' : 'View all podcasts'

    return new ListView(title, this.episodesView, { viewAllLink, viewAllText })
  }

  get podcastsListView(): ListView | null {
    if (!this.podcastsFound()) {
      return null
    }

    const title = this.search ? 'Podcasts' : 'Popular Podcasts'
    const viewAllLink = this.search
      ? `/app/podcasts/search/${encodeURI(this.search)}`
      : '/app/podcasts'

    return new ListView(title, this.podcastsView, { viewAllLink })
  }

  /*
   * Show the empty podcasts block if books or articles have been found but no podcasts.
   */
  get showEmptyPodcastBlock(): boolean {
    return (
      (this.articlesFound() || this.booksFound()) &&
      !this.episodesFound() &&
      !this.podcastsFound()
    )
  }

  booksFound(): this is KeyExists<typeof this, 'booksView'> {
    return this.booksView !== null && this.booksView.filteredDocs.length > 0
  }

  episodesFound(): this is KeyExists<typeof this, 'episodesView'> {
    if (!this.episodesView) {
      return false
    }
    return this.episodesView.filteredDocs.length > 0
  }

  podcastsFound(): this is KeyExists<typeof this, 'podcastsView'> {
    if (!this.podcastsView) {
      return false
    }
    return this.podcastsView.podcasts.length > 0
  }

  articlesFound(): this is KeyExists<typeof this, 'articlesView'> {
    return (
      this.articlesView !== null && this.articlesView.filteredDocs.length > 0
    )
  }

  get showEmptyBlock(): boolean {
    return (
      !this.booksFound() &&
      !this.articlesFound() &&
      !this.podcastsFound() &&
      !this.episodesFound()
    )
  }

  hasTags(): this is KeyExists<typeof this, 'tagsView'> {
    return this.tagsView !== null && this.tagsView.tags.length > 0
  }

  toggleFocus(): void {
    this.hasFocus = !this.hasFocus
  }

  clearSearchOnEscape(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.search = ''
    }
  }

  get headerClass(): any {
    return {
      'search__header--focus': !this.hasFocus || this.search,
      'search__header--scrolled': this.isScrolledDown,
      'search__header--ios': this.isIOS,
    }
  }

  /**
   * App scroll handler.
   *
   * When user scrolls on the mobile, we unfocus the search field to
   * hide a virtual keyboard.
   */
  private onAppScroll(appElement: HTMLElement): void {
    // 50 is roughly the height of the header in the Search page.
    // This is a little buffer zone to where we define the top
    // of the page, and it doesn't need to match exactly
    // the header height.
    this.isScrolledDown = appElement.scrollTop >= 50

    if (platform.isMobileWidth()) {
      nextTick(() => {
        this._vm.searchInput?.$el.blur()
      })
    }

    // Close header User menu on scroll, otherwise it looks buggy
    emitter.emit('closeOverlayMenu')
  }
}
